import React, { useMemo, useState } from 'react';
import { Input, Radio, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import intl from 'react-intl-universal';

import { RoomMainStatus } from '@/config/constant';

import styles from './index.module.scss';

const AllRoomSymbol = 'BILL_PICKER_ALL_ROOM_TYPES';

interface BillRoomItem {
  id: number; // 房间id
  roomType: string; // 房型名称
  roomNo: string; // 房间号
  mainStatus: RoomMainStatus; // 房态
  recNo: string; // 子单号
  resvNo: string; // 主单号
  customerName: string; // 房主、预订人姓名
  tagNo: string; // 联房
}

export interface BillRoomType {
  id: string; // 房型id
  name: string; // 房型名称
  roomList: BillRoomItem[];
}

interface IProps {
  dataSource: BillRoomType[];
  value: string;
  onChange: (val: string) => void;
}

const MainStatusMap = {
  [RoomMainStatus.Reserved]: {
    text: intl.get('reservation_cmp_room_item_reserve_label'),
    role: intl.get('app_constants_booker_name'),
    name: styles.statusReserved,
  },
  [RoomMainStatus.Occupied]: {
    text: intl.get('reservation_cmp_room_item_checkin_label'),
    role: intl.get('app_constants_master_name'),
    name: styles.statusOccupied,
  },
};

const Index: React.FC<IProps> = (props) => {
  const [searchText, setSearchText] = useState('');
  const [roomType, setRoomType] = useState(AllRoomSymbol);
  const { dataSource, value, onChange } = props;

  // 搜索房间号
  const handleTextChange = (event) => {
    setSearchText(event.target.value);
  };

  // 筛选房型
  const handleTypeChange = (event) => {
    setRoomType(event.target.value);
  };

  // 选中
  const handleRowSelect = (keys) => {
    const targetValue = keys[0];
    if (targetValue) {
      onChange(targetValue);
    }
  };

  // 房间信息
  const renderRoomNo = (record: BillRoomItem) => {
    const statusItem: typeof MainStatusMap['1'] = MainStatusMap[record.mainStatus] || {};
    return (
      <div className={styles.roomNoInfo}>
        <span className={statusItem.name}>{statusItem.text}</span>
        <span>{record.roomNo}</span>
        {record.tagNo ? (
          <span className={styles.joinTag}>
            {intl.get('app_reservation_room_join_tag', { tag: record.tagNo })}
          </span>
        ) : null}
      </div>
    );
  };

  // 宾客姓名
  const renderGuestName = (record: BillRoomItem) => {
    const statusItem: typeof MainStatusMap['1'] = MainStatusMap[record.mainStatus] || {};
    return (
      <div className={styles.roomGuestInfo}>
        <span className={styles.guestRoleTag}>{statusItem.role}</span>
        <span>{record.customerName}</span>
      </div>
    );
  };

  const tableColumns: ColumnsType<BillRoomItem> = [
    { title: intl.get('bill_transfer_room_order_no'), dataIndex: 'resvNo', width: 280 },
    { title: intl.get('app_constants_room_type'), dataIndex: 'roomType' },
    { title: intl.get('app_constants_room_no'), key: 'roomNo', render: renderRoomNo },
    { title: intl.get('bill_master_reservation_name'), key: 'id', render: renderGuestName },
  ];

  const pickerData = useMemo(() => {
    const list: BillRoomType[] = dataSource || [];
    const roomTypeList = [{ label: intl.get('app_checkbox_all'), value: AllRoomSymbol }];
    const roomItemList: BillRoomType['roomList'] = [];
    const isAllType = roomType === AllRoomSymbol;
    list.forEach((item) => {
      const { id, name, roomList } = item;
      roomTypeList.push({ label: name, value: id });
      (roomList || []).forEach((room) => {
        const roomNoText = room.roomNo || '';
        // 房间号 房型匹配
        if (roomNoText.includes(searchText) && (id === roomType || isAllType)) {
          roomItemList.push({
            ...room,
            roomType: name,
          });
        }
      });
    });
    return {
      roomTypeList,
      roomItemList,
    };
  }, [dataSource, roomType, searchText]);

  return (
    <>
      <div>
        <Input
          placeholder={intl.get('roomstate_room_linen_wash_room_modal_field_room_no')}
          allowClear
          value={searchText}
          onChange={handleTextChange}
        />
      </div>
      <div className={styles.billPickerWrapper}>
        <div className={styles.roomRadioGroup}>
          <Radio.Group
            optionType="button"
            options={pickerData.roomTypeList}
            value={roomType}
            onChange={handleTypeChange}
          />
        </div>
        <div className={styles.billTable}>
          <Table
            rowKey="recNo"
            rowSelection={{
              type: 'radio',
              selectedRowKeys: [value],
              onChange: handleRowSelect,
            }}
            columns={tableColumns}
            dataSource={pickerData.roomItemList}
            pagination={false}
          />
        </div>
      </div>
    </>
  );
};

export default Index;
